import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import { useCurrency } from "@/lib/currency";

export default function InvestorPortfolio() {
  const { t } = useTranslation();
  const { fmt } = useCurrency();
  const [holdings, setHoldings] = useState([]);

  useEffect(() => { api.get("/holdings/mine").then(({ data }) => setHoldings(data)).catch(()=>{}); }, []);

  const totalInvested = holdings.reduce((s, h) => s + h.total_invested, 0);
  const totalEarned   = holdings.reduce((s, h) => s + h.total_earned, 0);
  const monthlyEst    = holdings.reduce((s, h) => s + (h.ico ? h.coins_owned * h.ico.monthly_return_per_coin : 0), 0);

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-12">
      <div className="font-mono-label text-[11px] text-[#002FA7]">⎯⎯ {t("port_tag")}</div>
      <h1 className="font-display text-5xl font-black mt-2">{t("port_title")}</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-black/10 mt-10 border border-black/10">
        {[
          [t("port_invested"),  fmt(totalInvested)],
          [t("port_earned"),    fmt(totalEarned)],
          [t("port_next_month"), fmt(monthlyEst)],
        ].map(([k, v]) => (
          <div key={k} className="bg-white p-6 min-w-0">
            <div className="font-mono-label text-[10px] text-zinc-500 truncate">{k}</div>
            <div className="font-display text-2xl lg:text-3xl font-bold mt-2 tabular-nums truncate">{v}</div>
          </div>
        ))}
      </div>

      <div className="mt-12">
        <div className="font-mono-label text-[10px] text-zinc-500 mb-3">{t("port_holdings")}</div>
        {holdings.length === 0 ? (
          <div className="border border-black/10 p-12 text-center">
            <div className="font-display text-2xl font-bold">{t("port_empty")}</div>
            <Link to="/marketplace" className="font-mono-label text-xs text-[#002FA7] underline mt-2 inline-block">{t("port_browse")}</Link>
          </div>
        ) : (
          <div className="border border-black/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-black/10">
                <tr className="font-mono-label text-[10px]">
                  <th className="p-3 text-start">{t("port_col_property")}</th>
                  <th className="p-3 text-end">{t("port_col_coins")}</th>
                  <th className="p-3 text-end">{t("port_col_invested")}</th>
                  <th className="p-3 text-end">{t("port_col_earned")}</th>
                  <th className="p-3 text-end">{t("port_col_monthly")}</th>
                  <th className="p-3 text-end">{t("port_col_progress")}</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {holdings.map((h) => (
                  <tr key={h.id} data-testid={`holding-row-${h.id}`} className="border-b border-black/5 hover:bg-zinc-50">
                    <td className="p-3 font-medium">{h.property_name}</td>
                    <td className="p-3 text-end tabular-nums">{h.coins_owned.toLocaleString()}</td>
                    <td className="p-3 text-end tabular-nums whitespace-nowrap">{fmt(h.total_invested)}</td>
                    <td className="p-3 text-end text-[#002FA7] font-bold tabular-nums whitespace-nowrap">{fmt(h.total_earned)}</td>
                    <td className="p-3 text-end tabular-nums whitespace-nowrap">{h.ico ? fmt(h.coins_owned * h.ico.monthly_return_per_coin) : "—"}</td>
                    <td className="p-3 text-end">
                      {h.ico && (
                        <span className="font-mono-label text-[10px] whitespace-nowrap">
                          {h.ico.months_elapsed}/{h.ico.duration_months}
                          {" "}<Badge className={`rounded-none ms-2 ${h.ico.status === "funded" ? "bg-[#002FA7]" : h.ico.status === "expired" ? "bg-zinc-400" : "bg-black"}`}>{h.ico.status}</Badge>
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-end">
                      {h.ico && <Link to={`/ico/${h.ico.id}`} className="font-mono-label text-[10px] text-[#002FA7] underline">{t("mp_view_offering")}</Link>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
